import type { ConditionOperator } from '@reach/shared-types/conditions';
import React, { useEffect } from 'react';
import { getOperatorsForFieldType } from './field-type-operators';
import type { BuilderFieldType } from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  greater_than: 'greater than',
  less_than: 'less than',
  exists: 'exists',
  not_exists: 'does not exist',
  equals_month_day: 'Recurring Date',
  equals_month_day_year: 'Exact Date',
  between_month_day: 'Recurring Date Range',
  between_month_day_year: 'Exact Date Range',
  array_contains: 'array contains',
  contains: 'contains',
  not_contains: 'does not contain',
  in: 'is one of',
};

/**
 * Get the human-readable label for an operator, with special handling for date fields.
 * For date fields, "greater than" becomes "after" and "less than" becomes "before".
 */
const getOperatorLabel = (operator: ConditionOperator, fieldType: BuilderFieldType): string => {
  // Special case for date fields
  if (fieldType === 'date') {
    if (operator === 'greater_than') return 'after';
    if (operator === 'less_than') return 'before';
  }
  return OPERATOR_LABELS[operator] ?? operator;
};

interface OperatorSelectorProps {
  fieldType: BuilderFieldType;
  value: ConditionOperator;
  onChange: (value: ConditionOperator) => void;
  disabled?: boolean;
}

/**
 * Type-aware operator dropdown. Only shows operators valid for the selected field type.
 */
export const OperatorSelector: React.FC<OperatorSelectorProps> = ({
  fieldType,
  value,
  onChange,
  disabled = false,
}) => {
  const options = getOperatorsForFieldType(fieldType);
  const isValid = options.includes(value);
  const displayValue = isValid ? value : (options[0] ?? value);

  useEffect(() => {
    if (!isValid && options.length > 0 && options[0]) {
      onChange(options[0]);
    }
  }, [fieldType, isValid, options, onChange]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value as ConditionOperator);
  };

  return (
    <select
      className={styles.selectInput}
      value={displayValue}
      onChange={handleChange}
      disabled={disabled}
      aria-label="Condition operator">
      {options.map(op => (
        <option key={op} value={op}>
          {getOperatorLabel(op, fieldType)}
        </option>
      ))}
    </select>
  );
};
